import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const stats = [
  { value: '50+', label: 'Projets livrés',       accent: '#00f2fe' },
  { value: '98%', label: 'Clients satisfaits',    accent: '#4facfe' },
  { value: '5+',  label: "Années d'expérience",   accent: '#a855f7' },
  { value: '24/7', label: 'Support technique',    accent: '#ec4899' },
];

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24"
    >
      {/* ── Dot grid background ── */}
      <div className="absolute inset-0 hero-dot-grid pointer-events-none" />

      {/* ── Ambient halo — cyan, top-left ── */}
      <div
        className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full pointer-events-none animate-float-halo"
        style={{
          background: 'radial-gradient(circle, rgba(79,172,254,0.18) 0%, rgba(79,172,254,0.06) 40%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      {/* ── Ambient halo — violet, bottom-right ── */}
      <div
        className="absolute -bottom-20 -right-20 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(168,85,247,0.16) 0%, rgba(168,85,247,0.05) 45%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />

      {/* ── Ambient halo — center soft ── */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(79,172,254,0.08) 0%, transparent 65%)',
          filter: 'blur(60px)',
        }}
      />

      {/* ── 21st Magic radial bottom arc ── */}
      <div
        className="absolute left-1/2 top-[calc(100%-80px)] -translate-x-1/2 w-[140%] h-[500px] rounded-[100%] pointer-events-none"
        style={{
          background: 'radial-gradient(closest-side, rgba(7,19,44,0.92) 82%, rgba(79,172,254,0.10))',
          border: '1px solid rgba(79,172,254,0.07)',
        }}
      />

      {/* ── Content ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-[#4facfe]/[0.10] border border-[#4facfe]/30 mb-10 backdrop-blur-sm"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping-slow absolute inline-flex h-full w-full rounded-full bg-[#4facfe] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4facfe]" />
          </span>
          <Sparkles className="w-3.5 h-3.5 text-[#4facfe]" />
          <span className="text-white/85 text-sm font-medium tracking-wide">
            Innovation & Excellence
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.1, ease: 'easeOut' }}
          className="font-syne text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-extrabold tracking-tight text-white mb-6 leading-[1.05]"
        >
          Créons ensemble
          <br />
          <span className="text-gradient-animated">l'avenir numérique</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.2, ease: 'easeOut' }}
          className="text-white/72 text-base sm:text-lg lg:text-xl max-w-2xl mx-auto mb-12 leading-relaxed font-inter"
        >
          Solutions web et mobiles sur mesure pour entreprises ambitieuses.
          Du concept à la réalisation, nous transformons vos idées en expériences numériques exceptionnelles.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.3, ease: 'easeOut' }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            data-testid="cta-hero-button"
            className="group flex items-center gap-2 bg-gradient-to-r from-[#00f2fe] to-[#4facfe] text-[#030a1a] font-semibold rounded-xl px-9 h-14 text-base border-0 shadow-none animate-glow-pulse hover:opacity-90 active:scale-[0.96] transition-[transform,opacity] duration-150"
          >
            Commencer un projet
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </Button>

          <motion.a
            href="#portfolio"
            className="flex items-center gap-2 text-white/78 hover:text-white font-medium px-8 py-4 rounded-xl border border-white/18 hover:border-[#4facfe]/40 hover:bg-[#4facfe]/[0.06] backdrop-blur-sm transition-[border-color,background-color,color] duration-200 active:scale-[0.96]"
            whileTap={{ scale: 0.96 }}
          >
            Voir nos réalisations
          </motion.a>
        </motion.div>

        {/* ── Stats ── */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.4, ease: 'easeOut' }}
          className="mt-24 pt-10 border-t border-white/[0.08]"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.88 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1, ease: 'easeOut' }}
                className="text-center group"
              >
                <div
                  className="tabular-nums font-syne text-4xl sm:text-5xl font-black mb-2 transition-[filter] duration-300"
                  style={{
                    background: `linear-gradient(135deg, ${stat.accent}, #ffffff)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'drop-shadow(0 0 12px ' + stat.accent + '55)',
                  }}
                >
                  {stat.value}
                </div>
                <div className="text-white/58 text-sm font-inter">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#07132c] to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
